// src/hooks/useAuth.js
import { useState, useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase/config";

/**
 * Auth + role hook for the ADMIN panel.
 *
 * Firebase Auth only proves WHO you are, not WHAT role you are. The admin-vs-
 * fitter split lives in Firestore: a signed-in user with a `drivers/{uid}`
 * document is a FITTER; a signed-in user without one is an ADMIN (the same
 * signal the Firestore Security Rules use — see doc/firestore-rules-driver-role.md).
 *
 * This panel is admin-only. Resolving the role here lets AuthGuard bounce a
 * fitter to the Fitter Portal instead of dropping them on an empty dashboard
 * (a fitter's reads of the admin collections are denied by the rules, so the
 * admin UI renders blank).
 *
 * `isFitter` is true ONLY when we positively confirm a `drivers/{uid}` doc
 * exists. If the check errors we leave it false (fail open): the Firestore
 * rules still protect every collection, so the worst case is the previous
 * behaviour — never a lock-out of a genuine admin.
 */
export function useAuth() {
  const [user, setUser] = useState(null);
  const [isFitter, setIsFitter] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setUser(null);
        setIsFitter(false);
        setLoading(false);
        return;
      }

      let fitter = false;
      try {
        // A signed-in user may always read their OWN drivers/{uid} doc
        // (rules: allow read if request.auth.uid == driverId).
        const snap = await getDoc(doc(db, "drivers", u.uid));
        fitter = snap.exists();
      } catch (err) {
        console.error("[useAuth] role check failed; treating as non-fitter:", err);
        fitter = false;
      }

      setUser(u);
      setIsFitter(fitter);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return { user, isFitter, isAdmin: !!user && !isFitter, loading };
}
